import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    LogOut,
    CreditCard,
    Download,
    FileText,
    Globe,
    Award,
    ClipboardCheck,
    RefreshCw,
    XCircle,
    GraduationCap,
    BookOpen,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { API } from "../lib/site";
import { PLAN_INFO, ASSESSMENT_AREAS, formatPrice } from "../lib/plans";
import { LEARNING_MODULES, LEARNING_STATUSES } from "../lib/learning";
import { HOTEL_TOKEN_KEY } from "./Join";

const AreaBar = ({ label, value }) => (
    <div className="rounded-2xl border border-white/10 bg-[#07211A] p-5" data-testid={`score-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <div className="flex items-center justify-between">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-[#FDFBF7]/70">{label}</span>
            <span className="font-display text-lg text-[#D9B96A]">{value ? `${value}/5` : "—"}</span>
        </div>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
                className="h-full rounded-full bg-[#C2A160] transition-[width] duration-700"
                style={{ width: value ? `${(value / 5) * 100}%` : "0%" }}
            />
        </div>
    </div>
);

const HotelDashboard = () => {
    const [hotel, setHotel] = useState(null);
    const [prices, setPrices] = useState({});
    const [description, setDescription] = useState("");
    const [published, setPublished] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);
    const [busyAction, setBusyAction] = useState("");
    const [learning, setLearning] = useState({});
    const navigate = useNavigate();
    const token = localStorage.getItem(HOTEL_TOKEN_KEY);
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    const logout = () => {
        localStorage.removeItem(HOTEL_TOKEN_KEY);
        navigate("/hotel/login");
    };

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/hotels/me`, authHeaders);
            setHotel(data);
            setDescription(data.description || "");
            setPublished(!!data.published);
            setLearning(data.learning_progress || {});
        } catch (err) {
            if (err.response?.status === 401) logout();
            else toast.error("Could not load your dashboard.");
        }
    };

    useEffect(() => {
        if (!token) {
            navigate("/hotel/login");
            return;
        }
        load();
        axios.get(`${API}/plans`).then((res) => setPrices(res.data.prices || {})).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!hotel) {
        return (
            <div className="grain flex min-h-screen items-center justify-center bg-[#04150D]">
                <p className="text-sm text-[#FDFBF7]/60">Loading your dashboard…</p>
            </div>
        );
    }

    const membership = hotel.membership || {};
    const active = membership.status === "active";
    const cancelled = membership.status === "cancelled";
    const assessment = hotel.assessment;
    const actions = hotel.actions || [];
    const planInfo = PLAN_INFO.find((p) => p.key === (membership.plan || hotel.plan));
    const planName = planInfo?.name || "GGH Membership";

    const steps = [
        { label: "Assessment", done: !!assessment },
        { label: "Improvement Plan", done: !!assessment },
        { label: "Actions", done: actions.some((a) => a.done) },
        { label: "Progress Review", done: false },
        { label: "Recognition Eligibility", done: !!hotel.recognition_eligible },
    ];

    const startCheckout = async (plan) => {
        setBusyAction(`pay-${plan}`);
        try {
            const { data } = await axios.post(
                `${API}/payments/checkout`,
                { plan, origin_url: window.location.origin },
                authHeaders
            );
            window.location.href = data.checkout_url;
        } catch (err) {
            setBusyAction("");
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not start checkout.");
        }
    };

    const cancelMembership = async () => {
        if (!window.confirm("Cancel your membership? Access continues until the end of your paid period, per GGH subscription terms.")) return;
        setBusyAction("cancel");
        try {
            await axios.post(`${API}/hotels/cancel`, {}, authHeaders);
            toast.success("Membership cancelled. Access continues until the end of your paid period.");
            await load();
        } catch {
            toast.error("Could not cancel membership.");
        } finally {
            setBusyAction("");
        }
    };

    const toggleAction = async (index, done) => {
        try {
            await axios.patch(`${API}/hotels/actions/${index}`, { done }, authHeaders);
            setHotel((h) => ({
                ...h,
                actions: h.actions.map((a, i) => (i === index ? { ...a, done } : a)),
            }));
        } catch {
            toast.error("Could not update the action.");
        }
    };

    const requestReport = async () => {
        setBusyAction("report");
        try {
            await axios.post(`${API}/hotels/reports/request`, {}, authHeaders);
            toast.success("Report requested — the GGH team will prepare your sustainability summary.");
        } catch {
            toast.error("Could not request a report right now.");
        } finally {
            setBusyAction("");
        }
    };

    const saveProfile = async () => {
        setSavingProfile(true);
        try {
            await axios.patch(`${API}/hotels/me`, { description, published }, authHeaders);
            toast.success(published ? "Your public GGH Hotel Profile is live." : "Profile saved.");
            await load();
        } catch {
            toast.error("Could not save your profile.");
        } finally {
            setSavingProfile(false);
        }
    };

    const setModuleStatus = async (moduleId, status) => {
        const key = `learn-${moduleId}`;
        setBusyAction(key);
        try {
            await axios.put(`${API}/hotels/me/learning/${moduleId}`, { status }, authHeaders);
            setLearning((l) => ({ ...l, [moduleId]: { status } }));
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not update module progress.");
        } finally {
            setBusyAction("");
        }
    };

    return (
        <main>
            <SEO title="Hotel Dashboard | Global Green Hotel" description="Your GGH sustainability dashboard." />
            <div className="grain min-h-screen bg-[#04150D] py-28">
                <div className="relative z-[2] mx-auto max-w-[1200px] px-5 sm:px-8">
                    {/* Header */}
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <p className="eyebrow-gold">GGH Hotel Dashboard</p>
                            <h1 className="font-display mt-2 text-3xl font-medium text-[#FDFBF7] sm:text-4xl" data-testid="dashboard-welcome">
                                Welcome, {hotel.hotel_name}
                            </h1>
                            <p className="mt-1 text-sm text-[#FDFBF7]/50">
                                {hotel.location} · {hotel.hotel_type} · {hotel.rooms} rooms
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button type="button" data-testid="dashboard-refresh" onClick={load} className="btn-outline-light !px-5 !py-2.5">
                                <RefreshCw size={15} aria-hidden="true" /> Refresh
                            </button>
                            <button type="button" data-testid="dashboard-logout" onClick={logout} className="btn-outline-light !px-5 !py-2.5">
                                <LogOut size={15} aria-hidden="true" /> Sign Out
                            </button>
                        </div>
                    </div>

                    {/* Membership */}
                    <section data-testid="membership-card" className="mt-10 rounded-2xl border border-[#C2A160]/30 bg-[#07211A] p-7 sm:p-9">
                        <div className="flex flex-wrap items-start justify-between gap-6">
                            <div>
                                <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">Membership</p>
                                <p className="font-display mt-2 text-2xl font-medium text-[#FDFBF7]">{planName}</p>
                                <div className="mt-3 flex flex-wrap items-center gap-3">
                                    <span
                                        data-testid="membership-status"
                                        className={`rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider ${
                                            active
                                                ? "bg-[#1A3B2B] text-[#D9B96A]"
                                                : cancelled
                                                    ? "border border-red-400/50 text-red-300"
                                                    : "border border-[#C2A160]/50 text-[#D9B96A]"
                                        }`}
                                    >
                                        {active ? "Active" : cancelled ? "Cancelled" : "Pending Payment"}
                                    </span>
                                    {membership.valid_until && (
                                        <span className="text-xs text-[#FDFBF7]/50">
                                            Valid until {new Date(membership.valid_until).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                                        </span>
                                    )}
                                    {hotel.recognized && (
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C2A160] px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#0A1711]">
                                            <Award size={11} aria-hidden="true" /> GGH Recognition
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {membership.receipt_url && (
                                    <a
                                        href={membership.receipt_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="membership-receipt"
                                        className="btn-outline-light !px-5 !py-2.5"
                                    >
                                        <Download size={15} aria-hidden="true" /> Receipt
                                    </a>
                                )}
                            </div>
                        </div>
                        {cancelled && (
                            <p className="mt-4 text-xs text-[#FDFBF7]/50">
                                Your membership is cancelled and remains usable until the end of the
                                paid period. Use Renew to reactivate.
                            </p>
                        )}
                        <p className="mt-4 text-xs leading-relaxed text-[#FDFBF7]/40">
                            GGH membership and recognition are not government certification,
                            accreditation, or regulatory approval.
                        </p>
                    </section>

                    {/* Progress pathway */}
                    <section data-testid="progress-pathway" className="mt-8 rounded-2xl border border-white/10 bg-[#07211A] p-7 sm:p-9">
                        <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">Your GGH Pathway</p>
                        <ol className="mt-6 grid gap-3 sm:grid-cols-5">
                            {steps.map((s, i) => (
                                <li
                                    key={s.label}
                                    data-testid={`pathway-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className={`rounded-xl border p-4 text-center ${
                                        s.done ? "border-[#C2A160]/50 bg-[#C2A160]/10" : "border-white/10 bg-[#04150D]/40"
                                    }`}
                                >
                                    <span className={`font-display block text-2xl ${s.done ? "text-[#D9B96A]" : "text-[#FDFBF7]/30"}`}>
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className={`mt-1 block text-[11px] font-extrabold uppercase tracking-wider ${s.done ? "text-[#FDFBF7]" : "text-[#FDFBF7]/45"}`}>
                                        {s.label}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </section>

                    {/* Assessment & scores */}
                    <section className="mt-8 grid gap-6 lg:grid-cols-3">
                        <div data-testid="overall-score-card" className="grain relative overflow-hidden rounded-2xl bg-[#C2A160] p-7 sm:p-9">
                            <p className="relative z-[2] text-xs font-extrabold uppercase tracking-[0.2em] text-[#0A1711]/70">
                                Overall Sustainability Score
                            </p>
                            <p className="font-display relative z-[2] mt-3 text-6xl font-medium text-[#0A1711]">
                                {assessment ? `${assessment.overall}%` : "—"}
                            </p>
                            <p className="relative z-[2] mt-3 text-xs leading-relaxed text-[#0A1711]/70">
                                {assessment
                                    ? `Completed ${new Date(assessment.completed_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`
                                    : "Complete your GGH Assessment to reveal your score."}
                            </p>
                            <Link
                                to="/hotel/assessment"
                                data-testid="dashboard-start-assessment"
                                className="btn-green relative z-[2] mt-6 !px-5 !py-2.5"
                            >
                                <ClipboardCheck size={15} aria-hidden="true" />
                                {assessment ? "Retake Assessment" : "Start Your Assessment"}
                            </Link>
                            {!active && (
                                <p className="relative z-[2] mt-3 text-[11px] font-semibold text-[#0A1711]/60">
                                    Unlocks once your membership is active.
                                </p>
                            )}
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-[#07211A] p-7 sm:p-9 lg:col-span-2">
                            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">Assessment Results by Area</p>
                            <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="area-scores">
                                {ASSESSMENT_AREAS.map((a) => (
                                    <AreaBar key={a.key} label={a.label} value={assessment?.scores?.[a.key]} />
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Recommended actions + reports */}
                    <section className="mt-8 grid gap-6 lg:grid-cols-3">
                        <div className="rounded-2xl border border-white/10 bg-[#07211A] p-7 sm:p-9 lg:col-span-2" data-testid="recommended-actions">
                            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">Recommended Actions</p>
                            {actions.length === 0 ? (
                                <p className="mt-5 text-sm leading-relaxed text-[#FDFBF7]/60">
                                    Your personalized improvement plan appears here after you
                                    complete the GGH Assessment.
                                </p>
                            ) : (
                                <ul className="mt-5 grid gap-3">
                                    {actions.map((a, i) => (
                                        <li key={i} className="flex items-start gap-4 rounded-xl border border-white/10 bg-[#04150D]/40 p-5">
                                            <input
                                                type="checkbox"
                                                data-testid={`action-${i}`}
                                                checked={a.done}
                                                onChange={(e) => toggleAction(i, e.target.checked)}
                                                aria-label={`Mark "${a.label}" action as done`}
                                                className="mt-1 h-5 w-5 shrink-0 accent-[#C2A160]"
                                            />
                                            <div>
                                                <p className={`text-xs font-extrabold uppercase tracking-wider ${a.done ? "text-[#FDFBF7]/40 line-through" : "text-[#D9B96A]"}`}>
                                                    {a.label}
                                                </p>
                                                <p className={`mt-1 text-sm leading-relaxed ${a.done ? "text-[#FDFBF7]/35 line-through" : "text-[#FDFBF7]/75"}`}>
                                                    {a.text}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="grid content-start gap-6">
                            <div className="rounded-2xl border border-white/10 bg-[#07211A] p-7" data-testid="reports-card">
                                <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">Reports</p>
                                <p className="mt-4 text-sm leading-relaxed text-[#FDFBF7]/60">
                                    Request a simple sustainability progress report summarizing your
                                    performance, improvements and measurable results.
                                </p>
                                <button
                                    type="button"
                                    data-testid="request-report"
                                    onClick={requestReport}
                                    disabled={busyAction !== ""}
                                    className="btn-outline-light mt-5 !px-5 !py-2.5 disabled:opacity-60"
                                >
                                    <FileText size={15} aria-hidden="true" /> Request Report
                                </button>
                            </div>
                            <div className="rounded-2xl border border-white/10 bg-[#07211A] p-7" data-testid="training-card">
                                <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">GGH Learning Hub</p>
                                <p className="mt-4 text-sm leading-relaxed text-[#FDFBF7]/60">
                                    Short, practical sustainability learning for your hotel team —
                                    track each module as Not Started, In Progress or Completed.
                                </p>
                                <a href="#learning-hub" data-testid="open-learning-hub" className="btn-outline-light mt-5 !px-5 !py-2.5">
                                    <GraduationCap size={15} aria-hidden="true" /> Open Learning Hub
                                </a>
                            </div>
                        </div>
                    </section>

                    {/* GGH Learning Hub */}
                    <section id="learning-hub" data-testid="learning-hub" className="mt-8 scroll-mt-28 rounded-2xl border border-white/10 bg-[#07211A] p-7 sm:p-9">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                                <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">GGH Learning Hub</p>
                                <h2 className="font-display mt-2 text-2xl font-medium text-[#FDFBF7] sm:text-3xl">
                                    Practical learning for more sustainable hospitality.
                                </h2>
                            </div>
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C2A160]/40 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#D9B96A]">
                                <BookOpen size={11} aria-hidden="true" /> Members Only
                            </span>
                        </div>
                        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-[#FDFBF7]/60">
                            The GGH Learning Hub provides participating hotel teams with short,
                            practical learning resources that help employees understand
                            sustainability and apply better practices in everyday hotel operations.
                        </p>

                        <div className="mt-7 grid gap-4 md:grid-cols-2">
                            {LEARNING_MODULES.map((m) => {
                                const status = learning[m.id]?.status || "not_started";
                                const scores = assessment?.scores || {};
                                const recommended =
                                    Object.keys(scores).length > 0 &&
                                    m.areas.some((a) => (scores[a] ?? 5) <= 3);
                                return (
                                    <article
                                        key={m.id}
                                        data-testid={`learning-module-${m.id}`}
                                        className="flex flex-col rounded-xl border border-white/10 bg-[#04150D]/40 p-6"
                                    >
                                        {recommended && (
                                            <span
                                                data-testid={`learning-recommended-${m.id}`}
                                                className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#C2A160]/15 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#D9B96A]"
                                            >
                                                Recommended from your assessment
                                            </span>
                                        )}
                                        <h3 className="font-display text-lg font-medium leading-snug text-[#FDFBF7]">
                                            {m.title}
                                        </h3>
                                        <p className="mt-2 flex-1 text-sm leading-relaxed text-[#FDFBF7]/60">
                                            {m.summary}
                                        </p>
                                        <p className="mt-3 text-xs italic leading-relaxed text-[#FDFBF7]/40">
                                            Short lessons, hotel examples, key actions and a short
                                            knowledge check are being prepared by GGH and will
                                            appear here.
                                        </p>
                                        <div
                                            className="mt-5 flex flex-wrap gap-2"
                                            role="group"
                                            aria-label={`Progress for ${m.title}`}
                                        >
                                            {LEARNING_STATUSES.map((s) => (
                                                <button
                                                    key={s.key}
                                                    type="button"
                                                    data-testid={`learning-status-${m.id}-${s.key}`}
                                                    onClick={() => setModuleStatus(m.id, s.key)}
                                                    disabled={busyAction === `learn-${m.id}`}
                                                    aria-pressed={status === s.key}
                                                    className={`rounded-full border px-3.5 py-1.5 text-[10px] font-extrabold uppercase tracking-wider transition-colors duration-200 disabled:opacity-50 ${
                                                        status === s.key
                                                            ? "border-[#C2A160] bg-[#C2A160]/15 text-[#D9B96A]"
                                                            : "border-white/15 text-[#FDFBF7]/50 hover:border-[#C2A160]/50 hover:text-[#FDFBF7]/80"
                                                    }`}
                                                >
                                                    {s.label}
                                                </button>
                                            ))}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        <p className="mt-7 text-xs font-bold uppercase tracking-[0.18em] text-[#C2A160]">
                            Learn → Apply → Improve → Measure Impact
                        </p>
                        <p className="mt-3 max-w-3xl text-xs leading-relaxed text-[#FDFBF7]/40">
                            GGH learning modules support everyday good practice. Completing a
                            module is not a GGH certification, accreditation or professional
                            qualification.
                        </p>
                    </section>

                    {/* Public profile */}
                    <section className="mt-8 rounded-2xl border border-white/10 bg-[#07211A] p-7 sm:p-9" data-testid="profile-card">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#C2A160]">
                                <Globe size={13} className="mr-1 inline" aria-hidden="true" /> GGH Hotel Profile
                            </p>
                            {hotel.published && (
                                <Link
                                    to={`/hotels/${hotel.id}`}
                                    data-testid="view-public-profile"
                                    className="text-xs font-bold text-[#D9B96A] hover:underline"
                                >
                                    View public profile →
                                </Link>
                            )}
                        </div>
                        <p className="mt-4 text-sm leading-relaxed text-[#FDFBF7]/60">
                            Share your sustainability initiatives and progress on a public GGH
                            profile. Only what you publish is visible — your scores appear only if
                            you choose to publish.
                        </p>
                        <textarea
                            data-testid="profile-description"
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="input-field mt-5 !bg-[#04150D] !text-[#FDFBF7] !border-white/15 resize-y"
                            placeholder="Describe your hotel's sustainability initiatives, progress and goals…"
                            maxLength={2000}
                        />
                        <div className="mt-5 flex flex-wrap items-center gap-5">
                            <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-[#FDFBF7]/80">
                                <input
                                    type="checkbox"
                                    data-testid="profile-published"
                                    checked={published}
                                    onChange={(e) => setPublished(e.target.checked)}
                                    className="h-4 w-4 accent-[#C2A160]"
                                />
                                Publish my hotel profile on the GGH website
                            </label>
                            <button
                                type="button"
                                data-testid="profile-save"
                                onClick={saveProfile}
                                disabled={savingProfile}
                                className="btn-gold !px-5 !py-2.5 disabled:opacity-60"
                            >
                                {savingProfile ? "Saving…" : "Save Profile"}
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </main>
    );
};

export default HotelDashboard;
